export const weekdays = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'] as const

export type YeastType = 'seche' | 'fraiche'

export type Schedule = {
  day: string
  hour: number
  minute: number
}

export type PatonCounts = {
  mega: number
  gros: number
  classique: number
  mini: number
}

export type PizzaOptions = {
  patons: PatonCounts
  hydration: number
  temperature: number
  force: number
  protein: number
  yeast: YeastType
  prepare: Schedule
  meal: Schedule
  pitcherWater: number
  pitcherYeast: number
}

export type DoughResult = {
  totalWeight: number
  flour: number
  water: number
  salt: number
  yeast: number
  waterTemperature: number
  waterYeasted: number
  waterClear: number
  maturationMin: number
  maturationMax: number
  recommendedFlour: number
  recommendedProtein: number
  recommendedPrepare: Schedule
  recommendedBalling: Schedule
}

export const defaultSchedule = (): Schedule => ({ day: 'dimanche', hour: 13, minute: 0 })

export const defaultPizzaOptions = (): PizzaOptions => ({
  patons: { mega: 0, gros: 0, classique: 4, mini: 0 },
  hydration: 65,
  temperature: 22,
  force: 300,
  protein: 13.1,
  yeast: 'seche',
  prepare: { day: 'samedi', hour: 20, minute: 0 },
  meal: defaultSchedule(),
  pitcherWater: 500,
  pitcherYeast: 5.5,
})

export const totalPatonWeight = (patons: PatonCounts): number =>
  patons.mega * 330 + patons.gros * 300 + patons.classique * 250 + patons.mini * 100

export const forceToProtein = (force: number): number =>
  Math.round((0.01852941 * force + 7.56764706) * 10) / 10

export const proteinToForce = (protein: number): number =>
  Math.max(Math.round(53.92296719 * protein - 407.86019971), 170)

export const maturationTime = (force: number): number =>
  (7.2626419 / 10000) * force ** 2 - 0.242298762 * force + 23.9107327

export const flourForMaturation = (time: number): number => {
  if (time <= 3.71) return 170
  if (time >= 500) return 1000
  const a = 7.2626419 / 10000
  const b = -0.242298762
  const c = 23.9107327
  return Math.round((-b + Math.sqrt(b ** 2 - 4 * a * (c - time))) / (2 * a))
}

const scheduleToMinutes = (schedule: Schedule): number => {
  const dayIndex = Math.max(0, weekdays.indexOf(schedule.day as (typeof weekdays)[number]))
  return dayIndex * 24 * 60 + schedule.hour * 60 + schedule.minute
}

export const elapsedHours = (prepare: Schedule, meal: Schedule): number => {
  let minutes = scheduleToMinutes(meal) - scheduleToMinutes(prepare)
  if (minutes < 0) minutes += 7 * 24 * 60
  return minutes / 60 || 18
}

const previousSchedule = (meal: Schedule, hoursBack: number): Schedule => {
  let minutes = scheduleToMinutes(meal) - Math.round(hoursBack * 60)
  const week = 7 * 24 * 60
  while (minutes < 0) minutes += week
  const day = Math.floor(minutes / (24 * 60))
  const remaining = minutes % (24 * 60)
  return { day: weekdays[day], hour: Math.floor(remaining / 60), minute: remaining % 60 }
}

export const calculatePizza = (options: PizzaOptions): DoughResult => {
  const hydration = options.hydration / 100
  const saltRatio = 20 / 1000
  const totalWeight = totalPatonWeight(options.patons)
  const water = totalWeight / (1 + 1 / hydration + saltRatio)
  const flour = water / hydration
  const salt = saltRatio * water
  const time = elapsedHours(options.prepare, options.meal)
  const yeastCoefficient = options.yeast === 'fraiche' ? 3 : 1
  const yeast =
    ((flour * 2250 * (1 + salt / 200)) /
      ((-80 + 4.2 * hydration * 100 - 0.0305 * hydration ** 2) * options.temperature ** 2.5 * time ** 1.2)) *
    yeastCoefficient
  const pitcherRatio = options.pitcherYeast / Math.max(options.pitcherWater, 1)
  const waterYeasted = Math.round(yeast / pitcherRatio)
  const recommendedFlour = flourForMaturation(time - 2)
  const maturation = Math.round(maturationTime(options.force))

  return {
    totalWeight,
    flour: Math.round(flour),
    water: Math.round(water),
    salt: Math.round(salt * 10) / 10,
    yeast: Math.round(yeast * 100) / 100,
    waterTemperature: 24 * 3 - 2 * options.temperature - 9,
    waterYeasted,
    waterClear: Math.round(water) - waterYeasted,
    maturationMin: maturation,
    maturationMax: maturation + 4,
    recommendedFlour,
    recommendedProtein: forceToProtein(recommendedFlour),
    recommendedPrepare: previousSchedule(options.meal, Math.round(maturationTime(options.force) + 2)),
    recommendedBalling: previousSchedule(options.meal, -options.temperature + 29),
  }
}

export type PoolishOptions = {
  patons: PatonCounts
  hydration: number
  poolishPercentage: number
}

export type PoolishResult = {
  totalWeight: number
  flour: number
  water: number
  salt: number
  poolishWeight: number
  poolishWater: number
  poolishFlour: number
  poolishYeast: number
  poolishHoney: number
  remainingWater: number
  remainingFlour: number
}

export const defaultPoolishOptions = (): PoolishOptions => ({
  patons: { mega: 0, gros: 0, classique: 4, mini: 0 },
  hydration: 65,
  poolishPercentage: 50,
})

export const calculatePoolish = (options: PoolishOptions): PoolishResult => {
  const hydration = options.hydration / 100
  const saltRatio = 20 / 1000
  const totalWeight = totalPatonWeight(options.patons)
  const water = totalWeight / (1 + 1 / hydration + saltRatio)
  const flour = water / hydration
  const salt = saltRatio * water
  const poolishWeight = Math.round((totalWeight * options.poolishPercentage) / 100)
  const poolishFlour = Math.round(poolishWeight / 2)
  const poolishWater = poolishWeight - poolishFlour
  const poolishScale = poolishWeight / 600

  return {
    totalWeight,
    flour: Math.round(flour),
    water: Math.round(water),
    salt: Math.round(salt * 10) / 10,
    poolishWeight,
    poolishWater,
    poolishFlour,
    poolishYeast: Math.round(5 * poolishScale * 10) / 10,
    poolishHoney: Math.round(5 * poolishScale * 10) / 10,
    remainingWater: Math.round(water - poolishWater),
    remainingFlour: Math.round(flour - poolishFlour),
  }
}
